// Eskom PARCC — the pure reconcile PLANNER for the findings write path
// (RTM Workstream I, gap lifecycle, Sep 2026).
//
// The findings write path used to DELETE every finding on a transaction and
// re-insert the fresh capture. That FORGOT closures: a gap the buyer fixed simply
// vanished (the transaction read Compliant with no trace), so RTM Table 1's "gaps
// addressed" and the deck's gapsAddressed could never count buyer fixes.
//
// This planner replaces that with a reconcile. Given the findings CURRENTLY on a
// transaction (`existing`) and the findings a capture / editor save wants
// persisted (`incoming`), it decides — with NO Firebase — what to:
//   • KEEP / UPDATE  — a criterion still present is updated IN PLACE (its id,
//                      createdAt and firstRaisedAt are preserved).
//   • CLOSE          — a criterion no longer flagged is KEPT and marked Closed
//                      (closedAt + closedVia), never deleted, so the buyer fix is
//                      recorded. `findingDisposition` already reads Closed as
//                      "resolved" (R9), so it drops out of the outstanding count.
//   • INSERT         — a criterion not seen before is inserted fresh.
//
// Matching is the canonical (transaction × criterion) dedup rule
// ({@link normalizeCriterion}); within one transaction that reduces to the
// normalised criterion. Criterion-less rows (Additional-comments notes) carry no
// lifecycle — they are simply replaced (old removed, new inserted).
//
// PURE: no Firebase, no React. `reconcileFindingsForTx` (dbWrite) applies the plan.
import type { FindingRead } from './db';
import type { FindingInsertInput, FindingUpdateInput } from './dbWrite';
import { normalizeCriterion } from '../domain/findings';
import { normalizeFindingStatus } from '../domain/normalize';

/** How a finding was closed — the `Finding.closedVia` vocabulary. */
export type ReconcileClosedVia = 'buyer-fix' | 'override' | 'self-declaration' | 'recapture';

/**
 * The minimal existing-finding shape the planner reads (the `FindingsForTx` read):
 * enough to match by criterion and decide the closed state. A full {@link FindingRead}
 * satisfies it.
 */
export type ReconcileExisting = Pick<
  FindingRead,
  'id' | 'referenceNo' | 'criterion' | 'legacyStatus' | 'effectiveStatus' | 'closedAt' | 'closedVia' | 'deleted'
>;

export interface ReconcilePlan {
  /** Brand-new findings (a criterion not previously on the transaction) + all
   *  incoming criterion-less notes. */
  inserts: FindingInsertInput[];
  /** Findings to update in place, by real id: a matched criterion refreshed, or a
   *  vanished criterion closed. */
  updates: { id: string; patch: FindingUpdateInput }[];
  /** Ids of criterion-less existing findings (notes) to remove — replaced, not kept. */
  deletes: string[];
}

export interface ReconcileOptions {
  /** Injected timestamp for a close (kept out of the pure logic for testability). */
  now: string;
  /** The reason to stamp on findings THIS reconcile closes because they vanished
   *  from the capture (default `buyer-fix`). A capture that carries a Compliance
   *  Override → Compliant passes `override`; a self-declared transaction passes
   *  `self-declaration`. Findings the caller already marked closed keep their own
   *  `closedVia`. */
  closedVia?: ReconcileClosedVia;
}

function isClosedStatus(status: string | undefined | null): boolean {
  const n = normalizeFindingStatus(status ?? '');
  return n === 'Closed' || n === 'Closed by Self-Declaration';
}

/** True when the INCOMING finding is a closure the caller already decided (an
 *  override cascade sets effectiveStatus=Closed + closedVia). */
function incomingIsClosure(f: FindingInsertInput): boolean {
  return f.closedVia != null || isClosedStatus(f.effectiveStatus);
}

/** True when the EXISTING row is already closed — so a vanished-criterion close is
 *  idempotent (its original closedAt is never bumped). */
function existingIsClosed(f: ReconcileExisting): boolean {
  return f.closedAt != null || isClosedStatus(f.effectiveStatus ?? f.legacyStatus);
}

/**
 * The mutable fields refreshed on an in-place update of a matched criterion — the
 * same columns an insert would write, minus the identity (`criterion` is the match
 * key) and the immutable `firstRaisedAt`/`createdAt`. Only defined fields are
 * carried; absent ones (e.g. AI provenance on a non-AI re-capture) leave the row's
 * value untouched, so a re-capture never wipes a prior confirmed proposal.
 */
function refreshPatch(inc: FindingInsertInput): FindingUpdateInput {
  return {
    text: inc.text,
    source: inc.source,
    legacyStatus: inc.legacyStatus,
    anchors: inc.anchors,
    suggestedCompliance: inc.suggestedCompliance,
    finalCompliance: inc.finalCompliance,
    rationale: inc.rationale,
    confidence: inc.confidence,
    route: inc.route,
    matchedRules: inc.matchedRules,
    effectiveStatus: inc.effectiveStatus,
    reviewNotes: inc.reviewNotes,
    regulatoryRefs: inc.regulatoryRefs,
    aiPfmaSection: inc.aiPfmaSection,
    aiCitations: inc.aiCitations,
    aiModel: inc.aiModel,
    aiRunId: inc.aiRunId,
  };
}

/**
 * Plan the reconcile of a transaction's findings. Pure; matching is by the
 * canonical normalised criterion (within one transaction the (ref × criterion)
 * rule reduces to the criterion). See the file header for the four outcomes.
 */
export function planFindingReconcile(
  existing: readonly ReconcileExisting[],
  incoming: readonly FindingInsertInput[],
  opts: ReconcileOptions,
): ReconcilePlan {
  const { now } = opts;
  const defaultVia: ReconcileClosedVia = opts.closedVia ?? 'buyer-fix';
  const inserts: FindingInsertInput[] = [];
  const updates: { id: string; patch: FindingUpdateInput }[] = [];
  const deletes: string[] = [];

  // Index criterion-bearing, non-deleted existing findings by criterion; first wins
  // (a later duplicate is closed below, never matched).
  const live = existing.filter((f) => !f.deleted);
  const byKey = new Map<string, ReconcileExisting>();
  for (const f of live) {
    const key = normalizeCriterion(f.criterion);
    if (key && !byKey.has(key)) byKey.set(key, f);
  }

  const claimed = new Set<string>();

  for (const inc of incoming) {
    const key = normalizeCriterion(inc.criterion);
    const match = key ? byKey.get(key) : undefined;
    if (match && !claimed.has(match.id)) {
      claimed.add(match.id);
      const patch = refreshPatch(inc);
      if (incomingIsClosure(inc)) {
        patch.closedAt = inc.closedAt ?? now;
        patch.closedVia = inc.closedVia ?? defaultVia;
      } else {
        // Still non-compliant → clear any prior closure (a re-opened gap).
        patch.closedAt = null;
        patch.closedVia = null;
      }
      updates.push({ id: match.id, patch });
    } else {
      inserts.push(inc);
    }
  }

  for (const f of live) {
    if (claimed.has(f.id)) continue;
    const key = normalizeCriterion(f.criterion);
    if (!key) {
      // Criterion-less note — no lifecycle to keep; replaced by the incoming notes.
      deletes.push(f.id);
      continue;
    }
    // A criterion no longer flagged is KEPT and closed — unless already closed
    // (idempotent: never bump an existing closedAt).
    if (existingIsClosed(f)) continue;
    updates.push({
      id: f.id,
      patch: { effectiveStatus: 'Closed', closedAt: now, closedVia: defaultVia },
    });
  }

  return { inserts, updates, deletes };
}
